import { Link } from "react-router-dom";
import { useState } from "react";
import { Check, ArrowRight, Phone, MapPin, Filter, FileSignature, Users, ShieldCheck, Target, Clock } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import SEO from "../components/SEO";
import { apiClient, formatApiError } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { TEAM_PHOTO } from "../components/AboutTeam";

const H = { fontFamily: "Quicksand, sans-serif" };
const SERVICE_OPTIONS = ["Gestion complète", "Ménage et linge", "Accueil voyageurs", "Gestion des annonces", "Prix dynamiques", "Maintenance", "Photographie", "Biens haut de gamme"];
const STEPS = [
  { icon: Users, t: "Un propriétaire nous contacte", d: "Via nos 1308 pages communes, le simulateur ou le blog." },
  { icon: Phone, t: "Nous le rappelons", d: "Notre équipe qualifie son projet : bien, commune, services attendus, délai." },
  { icon: Filter, t: "Nous vous l'envoyons", d: "Uniquement si sa demande correspond à votre secteur et à vos services." },
  { icon: FileSignature, t: "Vous signez le mandat", d: "Vous le contactez avec toutes les infos : à vous de convaincre." },
];
const PLAN = [
  "Demandes de propriétaires de votre secteur",
  "Chaque demande qualifiée par téléphone",
  "Envoi selon votre zone et vos services",
  "Fiche complète : bien, services, coordonnées",
  "Sans engagement, résiliable à tout moment",
];
const FAQ = [
  { q: "Combien de demandes vais-je recevoir ?", a: "Cela dépend de votre secteur, de la saison et des services que vous proposez. Nous ne vous envoyons que des demandes qui correspondent vraiment à votre activité : mieux vaut moins de contacts, mais des propriétaires prêts à signer." },
  { q: "Pourquoi les places sont-elles limitées ?", a: "Pour que chaque conciergerie partenaire reçoive suffisamment de demandes, nous limitons le nombre de partenaires par secteur. Une fois le secteur complet, les nouvelles candidatures passent en liste d'attente." },
  { q: "Comment se passe la facturation ?", a: "Après validation de votre candidature, nous vous appelons pour définir votre secteur et vos services, puis l'abonnement de 99 € par mois vous est facturé chaque mois." },
  { q: "Suis-je engagé ?", a: "Non. L'abonnement est sans engagement et résiliable à tout moment." },
  { q: "Mon nom apparaît-il sur le site ?", a: "Non. Le site est un comparateur neutre : les propriétaires ne voient pas de liste de conciergeries. C'est notre équipe qui les oriente vers vous quand leur projet correspond." },
];

export default function PartnerPage() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", city: "", zone: "", company_name: "",
    years_experience: 0, properties_managed: 0, services: [], motivation: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const toggle = (s) => set({ services: form.services.includes(s) ? form.services.filter((x) => x !== s) : [...form.services, s] });
  const toForm = () => document.getElementById("candidature")?.scrollIntoView({ behavior: "smooth" });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post("/partners", { ...form, plan: "Réseau 99 €/mois" });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <SEO
        title="Conciergeries : recevez des propriétaires qualifiés dans votre secteur"
        description="Conciergerie Airbnb en Bretagne nord ? Rejoignez le réseau Bretagne Conciergerie Comparateur : demandes de propriétaires qualifiées par téléphone, dans votre secteur, pour 99 € par mois sans engagement."
      />
      <Toaster position="top-right" richColors />

      <section className="pt-8 pb-16 md:pb-20" data-testid="partner-hero">
        <div className="container-tm grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="pill">Espace conciergeries</span>
            <h1 className="h-display mt-5">Recevez des propriétaires qualifiés, dans votre secteur.</h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Nous attirons les propriétaires qui cherchent une conciergerie partout en Bretagne, de Saint-Malo à Brest et de Lannion à Vannes.
              Nous les rappelons, nous comprenons leur projet, puis nous vous les envoyons quand leur demande correspond à votre activité.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={toForm} className="btn-accent" data-testid="partner-hero-cta">Réserver ma place <ArrowRight size={16} /></button>
              <a href="#offre" className="btn-ghost" data-testid="partner-offer-link">Voir l&apos;offre</a>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <img src={TEAM_PHOTO} alt="L'équipe Bretagne Conciergerie Comparateur" className="w-full aspect-[4/3] object-cover blob" />
            <div className="absolute -bottom-6 left-4 sm:-left-6 bg-white surface-card px-6 py-4" data-testid="partner-trust-badge">
              <div className="text-3xl font-bold text-accent" style={H}>54</div>
              <div className="text-sm text-muted-foreground">conciergeries du secteur<br />nous font déjà confiance</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 section-beige mx-3 md:mx-6" data-testid="partner-stats">
        <div className="container-tm grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Users, v: "54", l: "conciergeries partenaires" },
            { icon: MapPin, v: "1308", l: "communes de Bretagne couvertes" },
            { icon: Phone, v: "100%", l: "demandes qualifiées par téléphone" },
            { icon: Target, v: "1 secteur", l: "places limitées par zone" },
          ].map((s) => (
            <div key={s.l} className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-white text-[hsl(var(--ocean))] flex items-center justify-center shrink-0 shadow-sm"><s.icon size={18} /></span>
              <div><div className="text-2xl font-bold text-primary" style={H}>{s.v}</div><div className="text-sm text-muted-foreground">{s.l}</div></div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container-tm">
          <span className="pill">Fonctionnement</span>
          <h2 className="h-section mt-4">Des demandes triées pour vous</h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.t} className="surface-card p-7" data-testid={`partner-step-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 rounded-full bg-[hsl(var(--ocean))] text-white flex items-center justify-center"><s.icon size={18} /></span>
                  <span className="text-sm font-bold text-accent">0{i + 1}</span>
                </div>
                <h3 className="h-card mt-5 text-xl">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="offre" className="pb-20 md:pb-24 scroll-mt-28" data-testid="partner-offer">
        <div className="container-tm grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="pill">L&apos;offre</span>
            <h2 className="h-section mt-4">Un abonnement simple et transparent</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">Un seul tarif mensuel pour recevoir les demandes de votre secteur. Pas de frais d&apos;entrée, pas d&apos;engagement.</p>
            <div className="mt-6 flex items-start gap-3 rounded-2xl bg-[hsl(var(--ocean))]/10 p-4" data-testid="partner-limited">
              <Clock size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-primary"><strong>Places limitées par secteur.</strong> Quand un secteur est complet, les candidatures passent en liste d&apos;attente.</p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="blob bg-primary text-white p-8 md:p-12 relative overflow-hidden" data-testid="partner-plan">
              <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[hsl(var(--ocean))]/40" />
              <div className="relative">
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-[hsl(var(--ocean))]">Abonnement Réseau</div>
                <div className="mt-3 flex items-end gap-2"><span className="text-6xl font-bold" style={H} data-testid="partner-price">99 €</span><span className="text-white/70 pb-2">/ mois</span></div>
                <div className="text-sm text-white/70 mt-1">Sans engagement · résiliable à tout moment</div>
                <ul className="mt-8 space-y-3">
                  {PLAN.map((p) => (
                    <li key={p} className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-[hsl(var(--ocean))] text-white flex items-center justify-center shrink-0"><Check size={13} strokeWidth={3} /></span>{p}</li>
                  ))}
                </ul>
                <button onClick={toForm} className="btn-sun mt-9" data-testid="partner-plan-cta">Réserver ma place dans mon secteur <ArrowRight size={16} /></button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-24">
        <div className="container-tm grid lg:grid-cols-3 gap-5">
          {[
            { icon: Target, t: "Des contacts qui vous correspondent", d: "Commune, type de bien, services attendus : vous ne recevez que les projets compatibles avec votre activité." },
            { icon: Phone, t: "Déjà qualifiés par téléphone", d: "Fini les formulaires vides : chaque propriétaire a été rappelé et attend votre appel." },
            { icon: ShieldCheck, t: "Un site neutre qui inspire confiance", d: "Les propriétaires viennent chercher un conseil, pas une publicité : ils sont plus ouverts à la signature." },
          ].map((b) => (
            <div key={b.t} className="flex gap-4">
              <b.icon size={24} className="text-accent shrink-0 mt-1" />
              <div><div className="font-bold text-lg" style={H}>{b.t}</div><p className="text-sm text-muted-foreground mt-1 leading-relaxed">{b.d}</p></div>
            </div>
          ))}
        </div>
        <div className="container-tm mt-10">
          <Link to="/badge-partenaire" className="inline-flex items-center gap-2 text-accent font-semibold hover:underline" data-testid="partner-badge-link">Déjà partenaire ? Récupérez votre badge pour votre site <ArrowRight size={16} /></Link>
        </div>
      </section>

      <section id="candidature" className="py-20 bg-[hsl(var(--ocean))] rounded-[2rem] mx-3 md:mx-6 scroll-mt-24">
        <div className="container-tm grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 text-white">
            <h2 className="h-section text-white">Candidater au réseau</h2>
            <p className="mt-4 text-white/80 leading-relaxed">Nous étudions votre candidature et vous rappelons pour définir votre secteur et vos services avant de démarrer.</p>
          </div>
          <div className="lg:col-span-8">
            {sent ? (
              <div className="bg-white rounded-3xl p-10 md:p-12 text-center" data-testid="partner-success">
                <div className="inline-flex w-16 h-16 rounded-full bg-accent/10 text-accent items-center justify-center"><Check size={30} strokeWidth={2.5} /></div>
                <h3 className="h-card text-2xl mt-4">Merci {form.first_name}, candidature reçue</h3>
                <p className="mt-2 text-muted-foreground">Nous vous rappelons rapidement pour vérifier les places disponibles dans votre secteur.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-3xl p-7 md:p-9 space-y-5" data-testid="partner-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Prénom *" value={form.first_name} onChange={(v) => set({ first_name: v })} testid="partner-first-name" required />
                  <Field label="Nom *" value={form.last_name} onChange={(v) => set({ last_name: v })} testid="partner-last-name" required />
                  <Field label="Email professionnel *" type="email" value={form.email} onChange={(v) => set({ email: v })} testid="partner-email" required />
                  <Field label="Téléphone *" value={form.phone} onChange={(v) => set({ phone: v })} testid="partner-phone" required />
                  <Field label="Nom de la conciergerie" value={form.company_name} onChange={(v) => set({ company_name: v })} testid="partner-company" />
                  <Field label="Ville du siège *" value={form.city} onChange={(v) => set({ city: v })} testid="partner-city" required />
                  <Field label="Biens gérés actuellement" type="number" value={form.properties_managed} onChange={(v) => set({ properties_managed: Number(v) })} testid="partner-properties" />
                  <Field label="Années d'expérience" type="number" value={form.years_experience} onChange={(v) => set({ years_experience: Number(v) })} testid="partner-years" />
                </div>
                <Field label="Secteur souhaité (communes) *" value={form.zone} onChange={(v) => set({ zone: v })} testid="partner-zone" required placeholder="Ex : Dinard, Saint-Lunaire, Saint-Briac" />
                <div>
                  <div className="text-sm font-semibold mb-2">Services proposés</div>
                  <div className="flex flex-wrap gap-2">
                    {SERVICE_OPTIONS.map((s) => (
                      <button type="button" key={s} onClick={() => toggle(s)} data-testid={`partner-service-${s}`}
                        className={`text-sm px-4 py-2 rounded-full border transition-colors ${form.services.includes(s) ? "bg-accent text-white border-accent" : "border-border hover:border-accent"}`}>{s}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold">Présentez votre conciergerie *</label>
                  <textarea required rows={4} value={form.motivation} onChange={(e) => set({ motivation: e.target.value })} data-testid="partner-motivation"
                    className="mt-2 w-full px-5 py-4 border border-input rounded-2xl bg-background focus:outline-none focus:ring-2 focus:ring-accent/30" />
                </div>
                <button type="submit" disabled={submitting} className="btn-accent w-full disabled:opacity-50" data-testid="partner-submit">
                  {submitting ? "Envoi…" : "Réserver ma place · 99 €/mois sans engagement"}
                </button>
                <p className="text-xs text-muted-foreground text-center">Vos données sont utilisées uniquement pour répondre à votre demande. <a href="/politique-de-confidentialite" className="underline">Politique de confidentialité</a></p>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="partner-faq">
        <div className="container-tm grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><span className="pill">FAQ</span><h2 className="h-section mt-4">Vos questions</h2></div>
          <Accordion type="single" collapsible className="lg:col-span-8">
            {FAQ.map((f, i) => (
              <AccordionItem key={f.q} value={`p${i}`} data-testid={`partner-faq-${i}`}>
                <AccordionTrigger className="text-left text-base md:text-lg font-semibold" style={H}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed text-base">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </PublicLayout>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, placeholder }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input type={type} required={required} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-2 w-full px-5 py-3.5 border border-input rounded-full bg-background focus:outline-none focus:ring-2 focus:ring-accent/30" />
    </div>
  );
}
